// Chantiers près de chez vous - Carte Interactive
// Style premium avec Leaflet

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin, X, ArrowLeft, Search, Filter, ChevronRight, Star,
  Building2, Calendar, Eye, MessageCircle, Store, User, Clock,
  Hammer, Droplets, Zap, Flame, Layers, Wind, TreeDeciduous,
  ShieldCheck, HardHat, Paintbrush, Ruler, CheckCircle2
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../../../components/ui/button';

import { API_URL } from '../../../config/api';

// Mock data for 10 Chantiers près de Nice
const MOCK_CHANTIERS_NICE = [
  {
    id: 'chantier-1',
    chantier_type: 'Remplacement colonne sèche résidence 12 appartements',
    chantier_metier: 'Plombier',
    city: 'Nice Centre',
    distance_km: 1.2,
    latitude: 43.7102,
    longitude: 7.2620,
    photos: ['https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600'],
    likes_count: 89,
    comments_count: 15,
    author: { display_name: 'Plomberie Azur', is_verified: true },
    chantier_status: 'en_cours'
  },
  {
    id: 'chantier-2',
    chantier_type: 'Pose carrelage terrasse 60m² grand format grès cérame',
    chantier_metier: 'Carreleur',
    city: 'Antibes',
    distance_km: 14,
    latitude: 43.5808,
    longitude: 7.1239,
    photos: ['https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=600'],
    likes_count: 134,
    comments_count: 23,
    author: { display_name: 'Carrelage Pro Côte', is_verified: true },
    chantier_status: 'en_cours'
  },
  {
    id: 'chantier-3',
    chantier_type: 'Remplacement chaudière → PAC air/eau maison individuelle',
    chantier_metier: 'Chauffagiste',
    city: 'Cannes',
    distance_km: 32,
    latitude: 43.5528,
    longitude: 7.0174,
    photos: ['https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=600'],
    likes_count: 178,
    comments_count: 32,
    author: { display_name: 'Chauf\'Expert 06', is_verified: true },
    chantier_status: 'termine'
  },
  {
    id: 'chantier-4',
    chantier_type: 'Ravalement façade enduit à la chaux immeuble 1930',
    chantier_metier: 'Façadier',
    city: 'Nice Est',
    distance_km: 2.8,
    latitude: 43.7196,
    longitude: 7.2850,
    photos: ['https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600'],
    likes_count: 156,
    comments_count: 28,
    author: { display_name: 'Façades Méditerranée', is_verified: true },
    chantier_status: 'en_cours'
  },
  {
    id: 'chantier-5',
    chantier_type: 'Extension ossature bois plain-pied 45m²',
    chantier_metier: 'Charpentier',
    city: 'Vence',
    distance_km: 18,
    latitude: 43.7225,
    longitude: 7.1119,
    photos: ['https://images.unsplash.com/photo-1513694203232-719a280e022f?w=600'],
    likes_count: 234,
    comments_count: 41,
    author: { display_name: 'Charpente Bois Alpes', is_verified: true },
    chantier_status: 'en_cours'
  },
  {
    id: 'chantier-6',
    chantier_type: 'Pose cuisine équipée + dressing sur mesure',
    chantier_metier: 'Menuisier',
    city: 'Cagnes-sur-Mer',
    distance_km: 9,
    latitude: 43.6640,
    longitude: 7.1480,
    photos: ['https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600'],
    likes_count: 312,
    comments_count: 56,
    author: { display_name: 'Menuiserie Riviera', is_verified: true },
    chantier_status: 'termine'
  },
  {
    id: 'chantier-7',
    chantier_type: 'Réfection toiture tuiles canal + zinguerie complète',
    chantier_metier: 'Couvreur',
    city: 'Menton',
    distance_km: 31,
    latitude: 43.7766,
    longitude: 7.5044,
    photos: ['https://images.unsplash.com/photo-1632778149955-e80f8ceca2e8?w=600'],
    likes_count: 145,
    comments_count: 19,
    author: { display_name: 'Toiture Côte d\'Azur', is_verified: true },
    chantier_status: 'en_cours'
  },
  {
    id: 'chantier-8',
    chantier_type: 'Création jardin méditerranéen avec piscine hors-sol',
    chantier_metier: 'Paysagiste',
    city: 'Cagnes-sur-Mer',
    distance_km: 9,
    latitude: 43.6590,
    longitude: 7.1530,
    photos: ['https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600'],
    likes_count: 267,
    comments_count: 45,
    author: { display_name: 'Jardins du Sud', is_verified: true },
    chantier_status: 'termine'
  },
  {
    id: 'chantier-9',
    chantier_type: 'Peinture intérieure appartement 75m² + ravalement',
    chantier_metier: 'Peintre',
    city: 'Nice Ouest',
    distance_km: 3.5,
    latitude: 43.7050,
    longitude: 7.2350,
    photos: ['https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=600'],
    likes_count: 98,
    comments_count: 12,
    author: { display_name: 'Peinture Pro Nice', is_verified: true },
    chantier_status: 'en_cours'
  },
  {
    id: 'chantier-10',
    chantier_type: 'Mise aux normes installation électrique villa années 70',
    chantier_metier: 'Électricien',
    city: 'Grasse',
    distance_km: 26,
    latitude: 43.6580,
    longitude: 6.9230,
    photos: ['https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=600'],
    likes_count: 189,
    comments_count: 34,
    author: { display_name: 'Élec Pro 06', is_verified: true },
    chantier_status: 'en_cours'
  }
];

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Icônes métiers
const METIER_ICONS = {
  'Pisciniste': { icon: Droplets, color: '#0EA5E9' },
  'Plombier': { icon: Droplets, color: '#3B82F6' },
  'Électricien': { icon: Zap, color: '#EAB308' },
  'Maçon': { icon: HardHat, color: '#6B7280' },
  'Chauffagiste': { icon: Flame, color: '#991B1B' },
  'Menuisier': { icon: Ruler, color: '#92400E' },
  'Façadier': { icon: Building2, color: '#8B5CF6' },
  'Paysagiste': { icon: TreeDeciduous, color: '#22C55E' },
  'Carreleur': { icon: Layers, color: '#10B981' },
  'Peintre': { icon: Paintbrush, color: '#EC4899' },
  'Couvreur': { icon: HardHat, color: '#1E3A5F' },
  'Charpentier': { icon: Hammer, color: '#78350F' },
};

// Filtres métiers
const METIERS_FILTER = [
  { id: 'all', label: 'Tous', icon: Building2 },
  { id: 'pisciniste', label: 'Piscine', icon: Droplets },
  { id: 'plombier', label: 'Plomberie', icon: Droplets },
  { id: 'electricien', label: 'Électricité', icon: Zap },
  { id: 'macon', label: 'Maçonnerie', icon: HardHat },
  { id: 'chauffagiste', label: 'Chauffage', icon: Flame },
  { id: 'menuisier', label: 'Menuiserie', icon: Ruler },
  { id: 'paysagiste', label: 'Jardins', icon: TreeDeciduous },
];

// Custom marker icon creator
const createCustomIcon = (metier, status) => {
  const metierInfo = METIER_ICONS[metier] || { color: '#2F4EA2' };
  const bgColor = status === 'termine' ? '#22C55E' : metierInfo.color;
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 36px;
        height: 36px;
        background: ${bgColor};
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        border: 3px solid white;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          font-size: 16px;
        ">📍</div>
      </div>
    `,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36],
  });
};

// Composant pour recentrer la carte
function MapController({ center, zoom }) {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || 10);
    }
  }, [center, zoom, map]);
  
  return null;
}

// Fiche détail d'un chantier (slide panel)
function ChantierDetailPanel({ chantier, onClose }) {
  const navigate = useNavigate();
  const metierInfo = METIER_ICONS[chantier?.chantier_metier] || { icon: Building2, color: '#2F4EA2' };
  const MetierIcon = metierInfo.icon;
  
  if (!chantier) return null;
  
  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white z-[1000] flex flex-col overflow-hidden shadow-2xl"
    >
      {/* Header avec image */}
      <div className="relative h-48 bg-gray-200">
        {chantier.photos?.[0] || chantier.media?.[0]?.url ? (
          <img
            src={chantier.photos?.[0] || chantier.media?.[0]?.url}
            alt={chantier.chantier_type}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
            <MetierIcon size={64} className="text-gray-300" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg"
        >
          <X size={20} className="text-gray-700" />
        </button>
        
        {/* Badge status */}
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
            chantier.chantier_status === 'termine' 
              ? 'bg-green-500 text-white' 
              : 'bg-orange-500 text-white'
          }`}>
            {chantier.chantier_status === 'termine' ? 'Terminé' : 'En cours'}
          </span>
        </div>
        
        {/* Title overlay */}
        <div className="absolute bottom-4 left-4 right-4">
          <h2 className="text-xl font-bold text-white mb-1">{chantier.chantier_type}</h2>
          <div className="flex items-center gap-2 text-white/90 text-sm">
            <MapPin size={14} />
            <span>{chantier.city}</span>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Entreprise */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {chantier.author?.profile_photo ? (
              <img
                src={chantier.author.profile_photo}
                alt={chantier.author.display_name}
                className="w-14 h-14 rounded-xl object-cover"
              />
            ) : (
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${metierInfo.color}20` }}
              >
                <MetierIcon size={28} style={{ color: metierInfo.color }} />
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-900">{chantier.author?.display_name || chantier.shop?.shop_name}</h3>
                {chantier.author?.is_verified && (
                  <CheckCircle2 size={16} className="text-blue-500" fill="#3B82F6" />
                )}
              </div>
              <p className="text-sm text-gray-500">{chantier.chantier_metier} • {chantier.city}</p>
              {chantier.shop?.rating_average && (
                <div className="flex items-center gap-1 mt-1">
                  <Star size={14} className="text-amber-400 fill-amber-400" />
                  <span className="text-sm font-medium">{chantier.shop.rating_average.toFixed(1)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Description */}
        {chantier.content && (
          <div className="p-4 border-b border-gray-100">
            <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">
              {chantier.content}
            </p>
          </div>
        )}
        
        {/* Timeline des étapes */}
        {chantier.chantier_steps && chantier.chantier_steps.length > 0 && (
          <div className="p-4 border-b border-gray-100">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Clock size={16} className="text-gray-400" />
              Suivi du chantier
            </h4>
            <div className="space-y-3">
              {chantier.chantier_steps.map((step, idx) => (
                <div key={step.id || idx} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                      style={{ backgroundColor: metierInfo.color }}
                    >
                      J{step.day}
                    </div>
                    {idx < chantier.chantier_steps.length - 1 && (
                      <div className="w-0.5 h-full bg-gray-200 mt-1" />
                    )}
                  </div>
                  <div className="flex-1 pb-3">
                    <h5 className="font-medium text-gray-900 text-sm">{step.title}</h5>
                    {step.description && (
                      <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
                    )}
                    {step.photos && step.photos.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {step.photos.slice(0, 2).map((photo, pIdx) => (
                          <img 
                            key={pIdx}
                            src={photo} 
                            alt={step.title}
                            className="w-16 h-16 rounded-lg object-cover"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Produits utilisés */}
        {chantier.linked_products && chantier.linked_products.length > 0 && (
          <div className="p-4 border-b border-gray-100">
            <h4 className="font-semibold text-gray-900 mb-3">Produits utilisés</h4>
            <div className="space-y-2">
              {chantier.linked_products.map((product, idx) => (
                <div 
                  key={product.id || idx}
                  className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg"
                >
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-10 h-10 object-contain" />
                    ) : (
                      <Layers size={20} className="text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm text-gray-900">{product.name}</p>
                    {product.price > 0 && (
                      <p className="text-xs text-gray-500">{product.price.toFixed(2)} €</p>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-gray-400" />
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Prestations associées */}
        {chantier.linked_services && chantier.linked_services.length > 0 && (
          <div className="p-4">
            <h4 className="font-semibold text-gray-900 mb-3">Prestations associées</h4>
            <div className="space-y-2">
              {chantier.linked_services.map((service, idx) => (
                <div 
                  key={service.id || idx}
                  className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Hammer size={18} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm text-gray-900">{service.name}</p>
                    {service.price > 0 && (
                      <p className="text-xs text-gray-500">À partir de {service.price.toFixed(2)} €</p>
                    )}
                  </div>
                  <ChevronRight size={16} className="text-gray-400" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer Actions */}
      <div className="p-4 border-t border-gray-200 bg-white">
        <div className="flex gap-3">
          <Button
            onClick={() => navigate(`/reseau/post/${chantier.id}`)}
            variant="outline"
            className="flex-1 rounded-xl"
          >
            <Eye size={16} className="mr-2" />
            Voir le chantier
          </Button>
          {chantier.shop?.slug && (
            <Button
              onClick={() => navigate(`/boutique/${chantier.shop.slug}`)}
              className="flex-1 rounded-xl"
              style={{ background: metierInfo.color }}
            >
              <Store size={16} className="mr-2" />
              Voir la boutique
            </Button>
          )}
        </div>
        <Button
          onClick={() => navigate(`/reseau/messages`)}
          variant="ghost"
          className="w-full mt-2 rounded-xl text-gray-600"
        >
          <MessageCircle size={16} className="mr-2" />
          Contacter l'artisan
        </Button>
      </div>
    </motion.div>
  );
}

// Page principale
export default function ChantiersMapPage() {
  const navigate = useNavigate();
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChantier, setSelectedChantier] = useState(null);
  const [selectedMetier, setSelectedMetier] = useState('all');
  const [mapCenter, setMapCenter] = useState([46.603354, 1.888334]); // Centre France
  const [mapZoom, setMapZoom] = useState(6);
  const [showFilters, setShowFilters] = useState(false);
  
  // Géolocalisation utilisateur
  const [userLocation, setUserLocation] = useState(null);
  
  useEffect(() => {
    // Demander la géolocalisation
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation([pos.coords.latitude, pos.coords.longitude]);
          setMapCenter([pos.coords.latitude, pos.coords.longitude]);
          setMapZoom(10);
        },
        () => {
          // Fallback: Paris
          setMapCenter([48.8566, 2.3522]);
          setMapZoom(8);
        }
      );
    }
  }, []);
  
  const fetchChantiers = useCallback(async () => {
    setLoading(true);
    try {
      // Always use mock data for demo (Nice region)
      setChantiers(MOCK_CHANTIERS_NICE);
      setMapCenter([43.7102, 7.2620]);
      setMapZoom(10);
    } catch (e) {
      console.error('Error fetching chantiers:', e);
      setChantiers(MOCK_CHANTIERS_NICE);
      setMapCenter([43.7102, 7.2620]);
      setMapZoom(10);
    } finally {
      setLoading(false);
    }
  }, [selectedMetier, userLocation]);
  
  useEffect(() => {
    fetchChantiers();
  }, [fetchChantiers]);
  
  return (
    <div className="min-h-screen bg-gray-100 relative" data-testid="chantiers-map-page">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-[1000] bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="px-4 py-3 flex items-center gap-3">
          <button 
            onClick={() => navigate('/reseau')}
            className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center"
          >
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          <div className="flex-1">
            <h1 className="font-bold text-gray-900">Chantiers près de chez toi</h1>
            <p className="text-xs text-gray-500">{chantiers.length} chantiers trouvés</p>
          </div>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              showFilters ? 'bg-[#2F4EA2] text-white' : 'bg-gray-100 text-gray-700'
            }`}
          >
            <Filter size={20} />
          </button>
        </div>
        
        {/* Filtres métiers */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden border-t border-gray-100"
            >
              <div className="px-4 py-3 flex gap-2 overflow-x-auto scrollbar-hide">
                {METIERS_FILTER.map((metier) => {
                  const Icon = metier.icon;
                  const isSelected = selectedMetier === metier.id;
                  return (
                    <button
                      key={metier.id}
                      onClick={() => setSelectedMetier(metier.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                        isSelected 
                          ? 'bg-[#2F4EA2] text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Icon size={16} />
                      {metier.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
      
      {/* Carte Leaflet */}
      <div className="absolute inset-0 pt-[60px]">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController center={mapCenter} zoom={mapZoom} />
          
          {/* Markers des chantiers */}
          {chantiers.map((chantier) => {
            if (!chantier.latitude || !chantier.longitude) return null;
            
            return (
              <Marker
                key={chantier.id}
                position={[chantier.latitude, chantier.longitude]}
                icon={createCustomIcon(chantier.chantier_metier, chantier.chantier_status)}
                eventHandlers={{
                  click: () => setSelectedChantier(chantier),
                }}
              >
                <Popup>
                  <div className="min-w-[200px]">
                    <div className="font-bold text-gray-900">{chantier.chantier_type}</div>
                    <div className="text-sm text-gray-600">{chantier.chantier_metier} • {chantier.city}</div>
                    {chantier.author?.display_name && (
                      <div className="text-xs text-gray-500 mt-1">{chantier.author.display_name}</div>
                    )}
                    <button 
                      onClick={() => setSelectedChantier(chantier)}
                      className="mt-2 w-full py-1.5 bg-[#2F4EA2] text-white text-xs rounded font-medium"
                    >
                      Voir les détails
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
          
          {/* Marker position utilisateur */}
          {userLocation && (
            <Marker 
              position={userLocation}
              icon={L.divIcon({
                className: 'user-location-marker',
                html: `
                  <div style="
                    width: 24px;
                    height: 24px;
                    background: #3B82F6;
                    border-radius: 50%;
                    border: 4px solid white;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                  "></div>
                `,
                iconSize: [24, 24],
                iconAnchor: [12, 12],
              })}
            />
          )}
        </MapContainer>
      </div>
      
      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-[999]">
          <div className="w-12 h-12 border-4 border-[#2F4EA2]/20 border-t-[#2F4EA2] rounded-full animate-spin" />
        </div>
      )}
      
      {/* Liste des chantiers en bas (mobile) */}
      <div className="absolute bottom-0 left-0 right-0 z-[500]">
        <div className="bg-white rounded-t-3xl shadow-2xl max-h-[40vh] overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-2" />
            <h3 className="font-semibold text-gray-900">Chantiers à proximité</h3>
          </div>
          <div className="overflow-y-auto max-h-[calc(40vh-60px)]">
            {chantiers.slice(0, 10).map((chantier) => {
              const metierInfo = METIER_ICONS[chantier.chantier_metier] || { icon: Building2, color: '#2F4EA2' };
              const MetierIcon = metierInfo.icon;
              const isTest = chantier.id?.startsWith('chantier-');
              
              return (
                <button
                  key={chantier.id}
                  onClick={() => {
                    setSelectedChantier(chantier);
                    if (chantier.latitude && chantier.longitude) {
                      setMapCenter([chantier.latitude, chantier.longitude]);
                      setMapZoom(14);
                    }
                  }}
                  className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors border-b border-gray-50 relative"
                >
                  {/* Badge TEST */}
                  {isTest && (
                    <div className="absolute top-2 right-2 bg-amber-500 text-white px-1.5 py-0.5 rounded text-[9px] font-bold flex items-center gap-0.5 z-10">
                      TEST
                    </div>
                  )}
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${metierInfo.color}15` }}
                  >
                    <MetierIcon size={24} style={{ color: metierInfo.color }} />
                  </div>
                  <div className="flex-1 text-left">
                    <h4 className="font-medium text-gray-900 text-sm line-clamp-1">{chantier.chantier_type}</h4>
                    <p className="text-xs text-gray-500">{chantier.chantier_metier} • {chantier.city}</p>
                    {chantier.distance_km !== undefined && (
                      <p className="text-xs text-blue-600 font-medium">à {chantier.distance_km} km</p>
                    )}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    chantier.chantier_status === 'termine' 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-orange-100 text-orange-700'
                  }`}>
                    {chantier.chantier_status === 'termine' ? 'Terminé' : 'En cours'}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Panel détail chantier */}
      <AnimatePresence>
        {selectedChantier && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[999]"
              onClick={() => setSelectedChantier(null)}
            />
            <ChantierDetailPanel 
              chantier={selectedChantier} 
              onClose={() => setSelectedChantier(null)} 
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
